//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback, useRef, useReducer, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import AddCircleIcon from '@material-ui/icons/AddCircle';
// import IconButton from '@material-ui/core/IconButton';
// import ButtonGroup from '@material-ui/core/ButtonGroup';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';
// import { /* Link DirectLink, Element, Events, scrollSpy, scroller*/ animateScroll as scroll } from 'react-scroll';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { LayoutContext } from '../../common/contexts';
import { /*AppContext,*/ LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { PLACEHOLDER_FIELD_RESUME_TITLE } from '../../common/placeholderHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveProfileResume } from '../../common/services/myProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import MessageFileBlock from '../../dashboard/subComponents/MessageFileBlock';
// import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdLinearProgress from '../../common/elemental/AfitdLinearProgress';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import DialogFieldLabel from './DialogFieldLabel';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import { format } from 'date-fns';
import _get from 'lodash/get';
import _fpSet from 'lodash/fp/set';
import _isNil from 'lodash/isNil';
import _some from 'lodash/some';
import _map from 'lodash/map';
import _slice from 'lodash/slice';
import _toUpper from 'lodash/toUpper';
import _last from 'lodash/last';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isError, attachment, uploadLinearProgressContainerIsHidden } = params;

    return makeStyles(theme => ({
        root: {},
        dialogContentContainer: {
            minHeight: '200px',
        },
        fieldContainer: {
            marginBottom: '24px',
        },
        subLabel: {
            fontWeight: 'normal',
        },
        resumeContainerExpanded: {
            border: isError ? '2px solid red' : '2px solid #1D8A8B',
            padding: '24px',
            borderRadius: '5px',
            margin: '0 0 0 0',
            marginBottom: '16px',
        },
        resumeHeaderContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '25px',
            marginBottom: '22px',
        },
        resumeHeaderLeft: {
            display: 'flex',
            margin: 0,
        },
        resumeHeaderRight: {
            textAlign: 'right',
        },
        selectedFileContainer: {
            display: !attachment ? 'none' : undefined,
        },
        uploadResumeDropZone: {
            display: !!attachment ? 'none' : 'flex',
            alignItems: 'center',
            border: '1px dashed #D5D7DD',
            borderRadius: '4px',
            padding: '13px 24px',
            // cursor: 'pointer',
        },
        uploadIconContainer: {
            color: 'rgba(17, 30, 20, 0.4)',
        },
        uploadInstructionContainer: {
            marginLeft: '16px',
        },
        uploadLinearProgressContainer: {
            visibility: uploadLinearProgressContainerIsHidden ? 'hidden' : 'visible',
        },
        deleteResumeButtonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            [theme.breakpoints.down('sm')]: { justifyContent: 'center' },
        },
        deleteResumeButton: {
            [theme.breakpoints.down('sm')]: { width: '100%' },
        },
        addResumeContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '82px',
            marginBottom: '16px',
            background: 'rgba(29, 138, 139, 0.06)',
            color: '#1D8A8B',
            cursor: 'pointer',
        },
        addResumeLabelContainer: {
            marginLeft: '10px',
        },
        descriptionHelperText: {
            textAlign: 'right',
        },
        hidden: {
            display: 'none',
        },
        invisible: {
            visibility: 'hidden',
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        isDirty,
        setIsDirty,
        closeDialog,
        clearFields,
        resumeList,
        resumeListDispatch,
        handleClickSaveButton,
        dialogContentRef,
        setIsRequestScrollToTop,
        setIsRequestScrollToResume2,
        setIsRequestScrollToResume3,
        setIsRequestScrollToResume4,
        isAddingNew,
        setIsAddingNew,
        errorMessage,
        handleCloseErrorMessageDialog,
        confirmMessage,
        setConfirmMessage,
        handleCloseConfirmDeleteAllDialog,
        initialActiveResumeIdx,
        setInitialActiveResumeIdx,
        saveLoading,
    } = params;
    const { open } = props;

    const isAllFilesValid = !_some(resumeList, item => !_isNil(item.attachment) && item.isValidFile === false);
    const resumeCount = resumeList.length;

    const defns = {
        root: {
            label: `Resume${resumeCount > 1 ? 's' : ''} ${resumeCount}/4`,
            open,
            isDirty,
            onClose: closeDialog,
            actionButtonDefns: {
                clearAll: {
                    label: 'Delete All',
                    disabled: resumeList.length === 0,
                    onClick: handleClickClearAllButton.bind(null, setConfirmMessage),
                },
                // cancel: { onClick: handleClickCancelButton.bind(null, isDirty, setIsConfirmCloseDialogOpen, closeDialog) },
                save: {
                    disabled: !isDirty || !isAllFilesValid,
                    loading: saveLoading,
                    onClick: handleClickSaveButton,
                },
            },
        },
        errorMessageDialog: {
            title: 'Error',
            open: !!errorMessage,
            message: errorMessage,
            onClose: handleCloseErrorMessageDialog,
        },
        confirmDeleteAllDialog: {
            title: 'Delete All Resumes?',
            open: !!confirmMessage,
            message: confirmMessage,
            onConfirm: handleConfirmDeleteAll.bind(null, clearFields),
            onClose: handleCloseConfirmDeleteAllDialog,
        },
        resume1: {
            idx: 0,
            data: _get(resumeList, '[0]'),
            dispatch: resumeListDispatch,
            progress: _get(resumeList, '[0].progress'),
            setIsDirty,
            setIsRequestScrollToTop,
            isAddingNew,
            setIsAddingNew,
            initialActiveResumeIdx,
            setInitialActiveResumeIdx,
        },
        resume2: {
            idx: 1,
            data: _get(resumeList, '[1]'),
            dispatch: resumeListDispatch,
            progress: _get(resumeList, '[1].progress'),
            setIsDirty,
            isHidden: _isNil(_get(resumeList, '[0]')),
            dialogContentRef,
            setIsRequestScrollToTop,
            setIsRequestScrollToResume: setIsRequestScrollToResume2,
            isAddingNew,
            setIsAddingNew,
            initialActiveResumeIdx,
            setInitialActiveResumeIdx,
        },
        resume3: {
            idx: 2,
            data: _get(resumeList, '[2]'),
            dispatch: resumeListDispatch,
            progress: _get(resumeList, '[2].progress'),
            setIsDirty,
            isHidden: _isNil(_get(resumeList, '[1]')),
            dialogContentRef,
            setIsRequestScrollToTop,
            setIsRequestScrollToResume: setIsRequestScrollToResume3,
            isAddingNew,
            setIsAddingNew,
            initialActiveResumeIdx,
            setInitialActiveResumeIdx,
        },
        resume4: {
            idx: 3,
            data: _get(resumeList, '[3]'),
            dispatch: resumeListDispatch,
            progress: _get(resumeList, '[3].progress'),
            setIsDirty,
            isHidden: _isNil(_get(resumeList, '[2]')),
            dialogContentRef,
            setIsRequestScrollToTop,
            setIsRequestScrollToResume: setIsRequestScrollToResume4,
            isAddingNew,
            setIsAddingNew,
            initialActiveResumeIdx,
            setInitialActiveResumeIdx,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

const handleClickClearAllButton = setConfirmMessage => {
    setConfirmMessage('All unsaved resumes will be lost.');
};

const handleConfirmDeleteAll = clearFields => {
    clearFields();
};

const getNewResume = (isActive = false) => {
    return {
        isActive,
        title: '',
        attachment: null,
        progress: null,
        isUpdated: true,
    };
};

const resumeListReducer = (state, action) => {
    const idx = _get(action, 'idx');
    const setIsDirty = _get(action, 'setIsDirty');

    switch (action.type) {
        case 'INIT': {
            const resumeListProp = _get(action, 'resumeListProp', []);
            const setIsInitialized = _get(action, 'setIsInitialized');
            const setInitialActiveResumeIdx = _get(action, 'setInitialActiveResumeIdx');
            let newState = null;
            if (resumeListProp.length === 0) {
                // no resume created yet
                newState = [getNewResume(true)];
            } else {
                // There is at least 1 resume.  Map the resume(s)
                newState = _map(resumeListProp, (item, itemIdx) => {
                    if (_get(item, 'isActive') === true) {
                        setInitialActiveResumeIdx(itemIdx);
                    }
                    return {
                        isActive: _get(item, 'isActive'),
                        title: _get(item, 'title'),
                        attachment: _get(item, 'attachment'),
                        progress: null,
                        updateDt: _get(item, 'updateDt'),
                    };
                });
            }
            setIsInitialized(true);
            return newState;
        }
        case 'ADD_RESUME': {
            const newState = _fpSet(`[${idx}]`, getNewResume(), state);
            setIsDirty(true);
            return newState;
        }
        case 'MODIFY_TITLE': {
            let newState = _fpSet(`[${idx}].title`, action.value, state);
            newState = _fpSet(`[${idx}].isUpdated`, true, newState);
            setIsDirty(true);
            return newState;
        }
        case 'MODIFY_FILE': {
            const selectedFile = action.selectedFile;
            let newAttachment = null;
            if (selectedFile) {
                const title = _get(selectedFile, 'name', '');
                // const size= _get(file, 'size', '');
                // const type= _get(file, 'type', '');
                newAttachment = { title, selectedFile };
            }
            let newState = _fpSet(`[${idx}].attachment`, newAttachment, state);
            newState = _fpSet(`[${idx}].isUpdated`, true, newState);
            setIsDirty(true);
            return newState;
        }
        case 'SET_ACTIVE': {
            if (state.length === 1) {
                const newState = _fpSet(`[${idx}].isActive`, true, state);
                return newState;
            } else {
                const newState = _map(state, (resume, resumeIdx) => {
                    return _fpSet('isActive', resumeIdx === idx, resume);
                });
                setIsDirty(true);
                return newState;
            }
        }
        case 'CLEAR_FILE': {
            const newState = _fpSet(`[${idx}].attachment`, null, state);
            setIsDirty(true);
            return newState;
        }
        case 'DELETE_RESUME': {
            // let newState = null;
            const setIsRequestScrollToTop = _get(action, 'setIsRequestScrollToTop');

            // // if last resume, initialize a new resume
            // if (state.length === 1) {
            //     newState = [getNewResume(true)];
            // } else {
            //     // remove resume from array
            //     newState = [..._slice(state, 0, idx), ..._slice(state, idx + 1)];
            //     // if no more active resume on the list, set the first one active
            //     if (!_some(newState, item => item.isActive === true)) {
            //         newState = _fpSet(`[0].isActive`, true, newState);
            //     }
            // }
            const newState = [..._slice(state, 0, idx), ..._slice(state, idx + 1)];

            setIsDirty(true);
            setIsRequestScrollToTop(true);
            return newState;
        }
        case 'DELETE_ALL': {
            const newState = [];
            setIsDirty(true);
            return newState;
        }
        case 'SET_PROGRESS': {
            const progress = _get(action, 'progress');
            const newState = _fpSet(`[${idx}].progress`, progress, state);
            return newState;
        }
        case 'SET_IS_VALID_FILE': {
            const isValidFile = _get(action, 'isValidFile');
            const newState = _fpSet(`[${idx}].isValidFile`, isValidFile, state);
            return newState;
        }
        case 'EMPTY_LIST': {
            return [];
        }
        case 'SET_ERROR': {
            const errorMessage = _get(action, 'errorMessage');
            let newState = _map(state, resume => _fpSet('error', null, resume));
            newState = _fpSet(`[${idx}].error`, errorMessage, newState);
            return newState;
        }
        case 'CLEAR_ERROR': {
            const newState = _map(state, resume => _fpSet('error', null, resume));
            return newState;
        }
        default:
            return state;
    }
};

const handleClickAddResume = (dispatch, idx, setIsDirty, setIsAddingNew, dialogContentRef) => {
    dispatch({ type: 'ADD_RESUME', idx, setIsDirty, dialogContentRef });
    setIsAddingNew(true);
};

const handleClickIsActive = (dispatch, idx, setIsDirty, evt) => {
    dispatch({ type: 'SET_ACTIVE', idx, setIsDirty });
};

const handleChangeResumeTitle = (dispatch, idx, setIsDirty, evt) => {
    dispatch({ type: 'MODIFY_TITLE', value: evt.target.value, idx, setIsDirty });
};

const handleChangeFile = (dispatch, idx, setIsDirty, evt) => {
    dispatch({ type: 'MODIFY_FILE', selectedFile: _get(evt, 'target.files[0]'), idx, setIsDirty });
};

const handleClearFile = (dispatch, idx, setIsDirty, evt) => {
    dispatch({ type: 'CLEAR_FILE', idx, setIsDirty });
};

const handleClickDeleteResumeButton = setConfirmMessage => {
    setConfirmMessage();
};

const handleConfirmDeleteResume = (dispatch, idx, setIsDirty, setIsRequestScrollToTop, evt) => {
    dispatch({ type: 'DELETE_RESUME', idx, setIsDirty, setIsRequestScrollToTop });
};

const handleCloseConfirmDeleteDialog = setConfirmMessage => {
    setConfirmMessage(null);
};

const isResumeFieldEmpty = resume => {
    const title = _get(resume, 'title');
    const attachment = _get(resume, 'attachment');
    return _isNil(title) || title === '' || _isNil(attachment);
};

const allowedFileTypes = ['.pdf', '.doc', '.docx'];

const validateFileType = (file, allowedFileTypes) => {
    const filename = _get(file, 'name', '');
    const extension = _toUpper(`.${_last(filename.split('.'))}`);
    const isTypeAllowed = _some(allowedFileTypes, item => _toUpper(item) === extension);
    return isTypeAllowed;
};

const validateFileSize = file => {
    const size = _get(file, 'size', 0);
    const isSizeAllowed = size <= 15000000; // 15MB
    return isSizeAllowed;
};

const ResumeForm = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        data,
        idx,
        progress,
        dispatch,
        setIsDirty,
        isAddingNew,
        setIsAddingNew,
        setIsRequestScrollToTop,
        setIsRequestScrollToResume,
        setResumeRef,
        initialActiveResumeIdx,
        setInitialActiveResumeIdx,
    } = props;

    const isActive = _get(data, 'isActive');
    const title = _get(data, 'title');
    const selectedFile = _get(data, 'attachment.selectedFile');
    const url = _get(data, 'attachment.url');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [confirmMessage, setConfirmMessage] = useState(false);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isActiveChecked = !!isActive;

    const selectedFileAttachment = {
        title: _get(selectedFile, 'name', ''),
        size: _get(selectedFile, 'size', ''),
        type: _get(selectedFile, 'type', ''),
    };

    let attachmentErrorMessage = null;
    if (selectedFile) {
        if (!validateFileType(selectedFile, allowedFileTypes)) {
            attachmentErrorMessage = 'The selected file is not allowed';
        } else if (!validateFileSize(selectedFile)) {
            attachmentErrorMessage = 'The selected file may not exceed 15MB';
        }
    }
    const isValidFile = !!!attachmentErrorMessage;

    const attachment = selectedFile ? selectedFileAttachment : _get(data, 'attachment');
    const selectedFileClickable = !!url && !!!selectedFile;

    const isError = !!_get(data, 'error');

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        if (setIsRequestScrollToResume) {
            if (initialActiveResumeIdx.current === idx || isAddingNew.current === true) {
                setIsRequestScrollToResume(true);
                setIsAddingNew(false);
                setInitialActiveResumeIdx(null);
            }
        }
    }, [setIsRequestScrollToResume, idx, initialActiveResumeIdx, setInitialActiveResumeIdx, isAddingNew, setIsAddingNew]);

    useEffect(() => {
        dispatch({ type: 'SET_IS_VALID_FILE', idx, isValidFile });
    }, [dispatch, idx, isValidFile]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isError, attachment, uploadLinearProgressContainerIsHidden: _isNil(progress) })();
    let defns = {
        resumeContainerExpanded: {},
        title: {
            // multiline: true,
            placeholder: PLACEHOLDER_FIELD_RESUME_TITLE,
            inputProps: { maxLength: 50 },
            value: title,
            onChange: handleChangeResumeTitle.bind(null, dispatch, idx, setIsDirty),
        },
        isActiveFormControlLabel: {
            value: 'active',
            control: <Radio />,
            label: 'Active',
            labelPlacement: 'start',
            checked: isActiveChecked,
            onClick: handleClickIsActive.bind(null, dispatch, idx, setIsDirty),
        },
        uploadResumeDropZone: {},
        selectFileInput: {
            type: 'file',
            style: { display: 'none' },
            onChange: handleChangeFile.bind(null, dispatch, idx, setIsDirty),
            accept: allowedFileTypes.join(', '),
        },
        selectedFileContainer: {},
        selectedFile: {
            attachment: attachment,
            isClickable: selectedFileClickable,
            onClear: handleClearFile.bind(null, dispatch, idx, setIsDirty),
        },
        attachmentErrorText: {
            clsRedColor: true,
            value: attachmentErrorMessage,
            isHidden: isValidFile,
        },
        uploadLinearProgressContainer: {},
        uploadLinearProgress: {
            variant: 'determinate',
            value: progress,
            // isHidden: _isNil(progress),
        },
        deleteResumeButton: {
            onClick: handleClickDeleteResumeButton.bind(null, setConfirmMessage.bind(null, `All unsaved data in Resume #${idx + 1} will be lost.`)),
        },
        confirmDeleteDialog: {
            title: `Delete Resume #${idx + 1}?`,
            open: !!confirmMessage,
            message: confirmMessage,
            onConfirm: handleConfirmDeleteResume.bind(null, dispatch, idx, setIsDirty, setIsRequestScrollToTop),
            onClose: handleCloseConfirmDeleteDialog.bind(null, setConfirmMessage),
        },
    };
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.resumeContainerExpanded} ref={setResumeRef}>
            <div {...defns.resumeHeaderContainer}>
                <div {...defns.resumeHeaderLeft}>
                    <AfitdTypography variant="body1" clsLightBold={true} value={`Resume: #${idx + 1}`} />
                </div>
                <div {...defns.resumeHeaderRight}>
                    <RadioGroup aria-label="isActive" name="isActive" value={'active'} onChange={() => {}}>
                        <FormControlLabel {...defns.isActiveFormControlLabel} />
                    </RadioGroup>
                </div>
            </div>
            <div {...defns.fieldContainer}>
                <DialogFieldLabel>Title</DialogFieldLabel>
                <AfitdTextField {...defns.title} />
            </div>
            <div {...defns.fieldContainer}>
                <DialogFieldLabel>Upload Resume</DialogFieldLabel>
                <div {...defns.uploadResumeDropZone}>
                    <div {...defns.uploadIconContainer}>
                        <CloudUploadIcon fontSize="large" />
                    </div>
                    <div {...defns.uploadInstructionContainer}>
                        {/* <AfitdTypography variant="body1">Drop your files here or click here to upload</AfitdTypography> */}
                        <Button
                            component="label"
                            disableRipple={true}
                            style={{
                                backgroundColor: 'transparent',
                                padding: 0,
                                textTransform: 'none',
                                // textDecoration: 'underline',
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '16px',
                                color: '#111E14',
                            }}
                        >
                            Click here to select file for upload
                            <input {...defns.selectFileInput} />
                        </Button>
                        <AfitdTypography variant="body2" clsGrayColor={true}>
                            PDF, DOC and DOCX are the accepted file types.
                        </AfitdTypography>
                        {/* ref={fileInputRef} */}
                    </div>
                </div>
                <div {...defns.selectedFileContainer}>
                    <MessageFileBlock {...defns.selectedFile} />
                    <AfitdTypography {...defns.attachmentErrorText} />
                    <div {...defns.uploadLinearProgressContainer}>
                        <AfitdLinearProgress {...defns.uploadLinearProgress} />
                    </div>
                </div>
            </div>
            <div {...defns.deleteResumeButtonContainer}>
                <AfitdButton {...defns.deleteResumeButton}>Delete</AfitdButton>
            </div>
            <AfitdConfirmDialog {...defns.confirmDeleteDialog} />
        </div>
    );
};

const AddResumeBlock = props => {
    const classes = useStyles(/*{param1, param2}*/)();
    const { idx, dispatch, setIsDirty, setIsAddingNew, dialogContentRef } = props;

    const defns = {
        addResumeContainer: {
            onClick: handleClickAddResume.bind(null, dispatch, idx, setIsDirty, setIsAddingNew, dialogContentRef),
        },
    };
    setClassNameInDefns(defns, classes);

    return (
        <div {...defns.addResumeContainer}>
            <AddCircleIcon />
            <div {...defns.addResumeLabelContainer}>
                <AfitdTypography variant="body1" clsLightBold={true} clsSecondaryColor={true}>
                    Add resume #{idx + 1}
                </AfitdTypography>
            </div>
        </div>
    );
};

const ResumeBlock = props => {
    // const classes = useStyles(/*{param1, param2}*/)();
    const { data, idx, isHidden, dispatch, setIsDirty, setIsAddingNew, dialogContentRef, setResumeRef } = props;

    if (_isNil(data)) {
        return isHidden ? null : <AddResumeBlock {...{ idx, dispatch, setIsDirty, setIsAddingNew, dialogContentRef, setResumeRef }} />;
    } else {
        return <ResumeForm {...props} />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const ResumeDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const context = useContext(LayoutContext);
    const userId = _get(context, 'userId');
    const setSnackbarMessage = _get(context, 'setSnackbarMessage');
    const setSnackbarErrorMessage = _get(context, 'setSnackbarErrorMessage');
    // const appContext = useContext(AppContext);
    // const lovs = _get(appContext, 'lovs');
    const dialogEl = useRef();
    const initialActiveResumeIdx = useRef();
    const dialogContentRef = useRef();
    const resume2BlockRef = useRef();
    const resume3BlockRef = useRef();
    const resume4BlockRef = useRef();
    const isAddingNew = useRef(false); // to track 'add new' so it can determine whether to scroll

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [resumeList, resumeListDispatch] = useReducer(resumeListReducer, []);
    const [isRequestScrollToTop, setIsRequestScrollToTop] = useState(false);
    const [isRequestScrollToResume2, setIsRequestScrollToResume2] = useState(false);
    const [isRequestScrollToResume3, setIsRequestScrollToResume3] = useState(false);
    const [isRequestScrollToResume4, setIsRequestScrollToResume4] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose, open } = props;
    const profileData = _get(props, 'data');

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const setContentRef = useCallback(ref => {
        dialogContentRef.current = ref;
    }, []);

    const setResume2Ref = useCallback(ref => {
        resume2BlockRef.current = ref;
    }, []);

    const setResume3Ref = useCallback(ref => {
        resume3BlockRef.current = ref;
    }, []);

    const setResume4Ref = useCallback(ref => {
        resume4BlockRef.current = ref;
    }, []);

    const setInitialActiveResumeIdx = useCallback(idx => {
        initialActiveResumeIdx.current = idx;
    }, []);

    const clearFields = useCallback(() => {
        resumeListDispatch({ type: 'DELETE_ALL', setIsDirty });
    }, []);

    const setProgress = useCallback((idx, progress) => {
        resumeListDispatch({ type: 'SET_PROGRESS', idx, progress });
    }, []);

    const closeDialog = useCallback(() => {
        clearFields(); // clear all fields when exit
        setIsDirty(false); // reset dirty flag
        onClose(); // fire onClose event
    }, [onClose, clearFields]);

    const handleClickSaveButton = useCallback(async () => {
        // validate
        let invalidResumeIdx = null;
        let errorMessage = null;
        const invalidResume = _some(resumeList, (resume, idx) => {
            if (resume.isActive === true && isResumeFieldEmpty(resume)) {
                errorMessage = `In order to set Resume #${idx + 1} as Active, you must enter a title and upload a resume.`;
                setErrorMessage(errorMessage);
                invalidResumeIdx = idx;
                return true;
            }
        });

        if (invalidResume) {
            // highlight the resume with error
            resumeListDispatch({ type: 'SET_ERROR', errorMessage, idx: invalidResumeIdx });
        } else {
            // clear highlight if any
            resumeListDispatch({ type: 'CLEAR_ERROR' });

            setSaveLoading(true);

            // save data
            await saveProfileResume({
                userId,
                resumeList,
                closeDialog,
                setProgress,
                setSnackbarMessage,
                setSnackbarErrorMessage,
            });

            setSaveLoading(false);
        }
    }, [userId, resumeList, closeDialog, setProgress, setSnackbarMessage, setSnackbarErrorMessage]);

    const setIsAddingNew = useCallback(value => {
        isAddingNew.current = value;
    }, []);

    const handleCloseErrorMessageDialog = useCallback(() => {
        setErrorMessage(null);
    }, []);

    const handleCloseConfirmDeleteAllDialog = useCallback(() => {
        setConfirmMessage(null);
    }, []);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        // when dialog is open, initialize data.
        // Since profileData is a dependency and there is a realtime update running, any db changes will
        // trigger this side effect.  That's why we need to set isInitialized to prevent it from re-initializing.
        if (open === true && !isInitialized) {
            const resumeListProp = _get(profileData, 'resumeList', []);
            resumeListDispatch({ type: 'INIT', resumeListProp, setIsInitialized, setInitialActiveResumeIdx });
        }
    }, [profileData, open, isInitialized, setInitialActiveResumeIdx]);

    useEffect(() => {
        // when closing dialog, clear the data and reset isInitialized
        if (open === false) {
            resumeListDispatch({ type: 'EMPTY_LIST' });
            setIsInitialized(false);
        }
    }, [open]);

    useEffect(() => {
        const dialogContentEl = _get(dialogContentRef, 'current');

        if (isRequestScrollToTop === true) {
            if (dialogContentEl) {
                dialogContentEl.scrollTo({ top: 0, behavior: 'smooth' });
                setIsRequestScrollToTop(false);
            }
        }

        if (isRequestScrollToResume2 === true) {
            if (dialogContentEl) {
                const resumeBlockScrollHeight = _get(resume2BlockRef, 'current.offsetTop', 1000);
                dialogContentEl.scrollTo({ top: resumeBlockScrollHeight - 64, behavior: 'smooth' });
                setIsRequestScrollToResume2(false);
            }
        }

        if (isRequestScrollToResume3 === true) {
            if (dialogContentEl) {
                const resumeBlockScrollHeight = _get(resume3BlockRef, 'current.offsetTop', 1000);
                dialogContentEl.scrollTo({ top: resumeBlockScrollHeight - 64, behavior: 'smooth' });
                setIsRequestScrollToResume3(false);
            }
        }

        if (isRequestScrollToResume4 === true) {
            if (dialogContentEl) {
                const resumeBlockScrollHeight = _get(resume4BlockRef, 'current.offsetTop', 1000);
                dialogContentEl.scrollTo({ top: resumeBlockScrollHeight - 64, behavior: 'smooth' });
                setIsRequestScrollToResume4(false);
            }
        }
    }, [isRequestScrollToTop, isRequestScrollToResume2, isRequestScrollToResume3, isRequestScrollToResume4]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        isDirty,
        setIsDirty,
        closeDialog,
        clearFields,
        resumeList,
        resumeListDispatch,
        handleClickSaveButton,
        dialogContentRef,
        setIsRequestScrollToTop,
        setIsRequestScrollToResume2,
        setIsRequestScrollToResume3,
        setIsRequestScrollToResume4,
        isAddingNew,
        setIsAddingNew,
        errorMessage,
        handleCloseErrorMessageDialog,
        confirmMessage,
        setConfirmMessage,
        handleCloseConfirmDeleteAllDialog,
        initialActiveResumeIdx,
        setInitialActiveResumeIdx,
        saveLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Fragment>
            <AfitdEditDialog ref={dialogEl} setContentRef={setContentRef} {...defns.root}>
                <div {...defns.dialogContentContainer}>
                    <div {...defns.fieldContainer}>
                        <DialogFieldLabel>
                            Resume: <span {...defns.subLabel}>(Add up to 4)</span>
                        </DialogFieldLabel>
                        <ResumeBlock {...defns.resume1} />
                        <ResumeBlock {...defns.resume2} setResumeRef={setResume2Ref} />
                        <ResumeBlock {...defns.resume3} setResumeRef={setResume3Ref} />
                        <ResumeBlock {...defns.resume4} setResumeRef={setResume4Ref} />
                    </div>
                </div>
            </AfitdEditDialog>
            <AfitdMessageDialog {...defns.errorMessageDialog} />
            <AfitdConfirmDialog {...defns.confirmDeleteAllDialog} />
        </Fragment>
    );
};

export default ResumeDialog;
